import axios from 'axios';

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID;
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const REGION = process.env.REACT_APP_REGION || 'us-east-2';

// Cognito endpoints
const COGNITO_URL = `https://cognito-idp.${REGION}.amazonaws.com/`;

class AuthService {
  constructor() {
    this.user = null;
    this.token = null;
    this.loadFromStorage();
  }

  loadFromStorage() {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('authUser');
    if (token && user) {
      this.token = token;
      this.user = JSON.parse(user);
    }
  }

  // Decode JWT token to check expiration
  isTokenExpired(token) {
    if (!token) return true;
    
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      const payload = JSON.parse(jsonPayload);
      const exp = payload.exp * 1000; // Convert to milliseconds
      
      return Date.now() >= exp;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }

  // Validate current token
  validateToken() {
    if (!this.token || this.isTokenExpired(this.token)) {
      this.logout();
      return false;
    }
    return true;
  }

  async login(email, password) {
    try {
      // Use AWS Cognito SDK or direct API call
      const response = await axios.post(
        COGNITO_URL,
        {
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: CLIENT_ID,
          AuthParameters: {
            USERNAME: email,
            PASSWORD: password,
          },
        },
        {
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
          },
        }
      );

      if (response.data.AuthenticationResult) {
        this.token = response.data.AuthenticationResult.IdToken;
        const accessToken = response.data.AuthenticationResult.AccessToken;
        
        // Get user info
        const userInfo = await this.getUserInfo(accessToken);
        this.user = {
          email: userInfo.email || email,
          username: userInfo.username || email,
        };

        // Save to localStorage
        localStorage.setItem('authToken', this.token);
        localStorage.setItem('authUser', JSON.stringify(this.user));

        return { success: true, user: this.user };
      }

      throw new Error('Authentication failed');
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  }

  async getUserInfo(accessToken) {
    try {
      const response = await axios.post(
        COGNITO_URL,
        {},
        {
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.GetUser',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const attributes = {};
      response.data.UserAttributes?.forEach((attr) => {
        attributes[attr.Name] = attr.Value;
      });

      return {
        username: response.data.Username,
        email: attributes.email,
        ...attributes,
      };
    } catch (error) {
      console.error('Get user info error:', error);
      return {};
    }
  }

  logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  }

  isAuthenticated() {
    return !!this.token;
  }

  getToken() {
    return this.token;
  }

  getUser() {
    return this.user;
  }
}

export default new AuthService();
